/**
 * Application_Form — phase-deadline decision pure logic
 * (season-phase-deadlines spec, task 1).
 *
 * This module is the single source of truth for the two per-Season phase
 * deadlines and the Director Manual_Lock_Override. It is **genuinely pure**: it
 * performs no I/O, reads no globals, and never calls `new Date()` — the current
 * instant is always injected as an explicit `now: Date` argument, exactly like
 * `seasonResolvers.ts`. Because it imports nothing backend-only, the identical
 * decision is consumed by both the Application_Form_Service (authoritative
 * enforcement) and the Applicant_Portal (client mirror), so server rejection and
 * client rendering can never disagree.
 *
 * The two deadlines it evaluates:
 *   - **Phase_1_Close_Date** = the Season's existing `intakeWindow.closesAt`.
 *     It is NOT a new attribute: `isPhase1Open` composes the SAME exclusive
 *     `closesAt` comparison as `isIntakeOpen` (`now >= closesAt` ⇒ closed), with
 *     no new Phase 1 code path.
 *   - **Phase_2_Due_Date** = a new nullable per-Season attribute. Phase 2 unlock
 *     composes the existing Approval_Gate (`isPhaseTwoUnlocked`) WITH the due
 *     date — it never replaces it.
 *
 * Date gating composes with — never replaces — the Approval_Gate. A separate,
 * Director-set Manual_Lock_Override (`LOCKED` / `UNLOCKED` / absent) takes
 * precedence over the date-based phase close when computing the effective
 * Application_Lock.
 *
 * Deadlines are stored as absolute ISO-8601 instants and compared against the
 * injected instant; the decision depends only on the absolute instant, never on
 * any local timezone (R7.3). A null/absent or unparseable deadline is treated as
 * "never reached / open", matching the `isIntakeOpen` back-compat behavior, so a
 * Season with neither deadline behaves exactly as today (R10).
 *
 * Validates: Requirements 4.1, 4.3, 4.4, 4.5, 5.1, 5.3, 5.4, 5.5, 6.1, 6.2, 6.3,
 * 6.4, 6.5, 7.3, 7.4, 7.5, 10.1, 10.2, 10.3, 10.4, 10.5, 16.1, 16.2, 16.3, 16.4,
 * 16.6, 17.1, 17.3, 17.4, 17.5, 18.7, 18.8
 */

import { SubmissionStatus } from '../../types/database';
import { isPhaseTwoUnlocked } from './phaseGating';

// ---------------------------------------------------------------------------
// Types

/**
 * Director-set manual override on a single Application. `LOCKED`/`UNLOCKED` take
 * precedence over the date-based phase close; an absent override (the third
 * state) means the date-based close alone governs.
 */
export type ManualLockOverride = 'LOCKED' | 'UNLOCKED';

/**
 * The two per-Season deadline inputs, sourced from the Season row. Both are
 * nullable: `null`/absent means "no date restriction" on that side.
 */
export interface PhaseDeadlineConfig {
  /** Season intake window `closesAt` (Phase_1_Close_Date). null/absent = open-ended. */
  phaseOneCloseAt: string | null;
  /** New per-Season Phase_2_Due_Date. null/absent = no Phase 2 date restriction. */
  phaseTwoDueDate: string | null;
}

/** Which phase an Application is currently in (drives which deadline is relevant). */
export type CurrentPhase = 'PHASE_1' | 'PHASE_2';

/** The full evaluated state consumed by both the server and the client mirror. */
export interface PhaseState {
  /** Phase 1 open on the close side: no close, or `now < close` (exclusive). */
  isPhase1Open: boolean;
  /** Approval_Gate AND (no due date OR `now < dueDate`). */
  isPhase2Unlocked: boolean;
  /** The Application's current phase: `PHASE_2` iff `APPROVED`, else `PHASE_1`. */
  currentPhase: CurrentPhase;
  /** Effective read-only lock after composing the date gate with the override. */
  effectiveLock: boolean;
}

// ---------------------------------------------------------------------------
// Deadline predicate (pure)

/**
 * Whether an instant is at or after a stored deadline (the deadline is reached).
 *
 * A `null`/absent or unparseable deadline is treated as "never reached" (the
 * phase stays open), matching the `isIntakeOpen` back-compat behavior. The
 * comparison is **exclusive** in the same sense as `isIntakeOpen`'s `closesAt`:
 * equality counts as reached, so `now >= deadline` ⇒ reached/closed and
 * `now < deadline` ⇒ open. The decision depends only on the absolute instant, so
 * two ISO-8601 strings that denote the same instant in different UTC offsets
 * yield the identical result (R7.3).
 *
 * Pure and total. (R4.1, R5.1, R7.4, R7.5.)
 */
export function isDeadlineReached(deadline: string | null, now: Date): boolean {
  if (deadline === null || deadline === undefined) return false;
  const deadlineMs = Date.parse(deadline);
  if (Number.isNaN(deadlineMs)) return false;
  return now.getTime() >= deadlineMs;
}

// ---------------------------------------------------------------------------
// Phase derivation and composition (pure)

/**
 * The Application's current phase from its approval status: `PHASE_2` iff the
 * Approval_Gate is satisfied (`applicationStatus === APPROVED`), else `PHASE_1`.
 * Composed from `isPhaseTwoUnlocked` so it can never disagree with the gate.
 *
 * Pure and total.
 */
export function currentPhaseOf(status: SubmissionStatus): CurrentPhase {
  return isPhaseTwoUnlocked(status) ? 'PHASE_2' : 'PHASE_1';
}

/**
 * Phase 2 unlock composing the Approval_Gate with the due date (R6.1–R6.4):
 *   `isPhaseTwoUnlocked(status)` AND `!isDeadlineReached(phaseTwoDueDate, now)`.
 *
 * Consequently a non-approved Application is locked regardless of the due date
 * or `now`, and an approved Application with no due date is unlocked for every
 * `now` (R5.5, R6.2, R6.3).
 *
 * Pure and total.
 */
export function isPhaseTwoUnlockedWithDate(
  status: SubmissionStatus,
  phaseTwoDueDate: string | null,
  now: Date,
): boolean {
  return isPhaseTwoUnlocked(status) && !isDeadlineReached(phaseTwoDueDate, now);
}

/**
 * The date-based lock for the Application's CURRENT phase, ignoring any override
 * (R16.4, R17.1):
 *   `PHASE_1` → `isDeadlineReached(phaseOneCloseAt, now)`
 *   `PHASE_2` → `isDeadlineReached(phaseTwoDueDate, now)`
 *
 * Selecting the current phase's deadline is what keeps a Phase 1 submit from
 * ever requiring a Phase 2 field to be complete (R6.5): in Phase 1 only the
 * Phase 1 close is consulted.
 *
 * Pure and total.
 */
export function dateBasedLock(
  config: PhaseDeadlineConfig,
  status: SubmissionStatus,
  now: Date,
): boolean {
  const deadline =
    currentPhaseOf(status) === 'PHASE_1'
      ? config.phaseOneCloseAt
      : config.phaseTwoDueDate;
  return isDeadlineReached(deadline, now);
}

/**
 * Effective Application_Lock composing the date gate with the override
 * (R16.1–R16.4). The override always takes precedence over the calendar:
 *   `UNLOCKED` → `false` (never locked)
 *   `LOCKED`   → `true`  (always locked)
 *   absent     → `dateBasedLock(config, status, now)`
 *
 * Pure and total.
 */
export function effectiveApplicationLock(
  config: PhaseDeadlineConfig,
  status: SubmissionStatus,
  manualLockOverride: ManualLockOverride | null | undefined,
  now: Date,
): boolean {
  if (manualLockOverride === 'UNLOCKED') return false;
  if (manualLockOverride === 'LOCKED') return true;
  return dateBasedLock(config, status, now);
}

/**
 * The full evaluated phase state consumed by both the server (authoritative
 * enforcement) and the client (mirror). Total and pure — it never mutates its
 * inputs and performs no state transition, so evaluating it against a decided
 * (`SUBMITTED`/`APPROVED`/`REJECTED`) Application never changes that Application
 * (R4.5).
 *
 *   - `isPhase1Open`   — composes the same exclusive `closesAt` comparison as
 *                        `isIntakeOpen`: open iff the close is null/absent or
 *                        `now < close` (R4.1, R4.3, R4.4).
 *   - `isPhase2Unlocked` — Approval_Gate AND due-date gate (R5, R6).
 *   - `currentPhase`   — `PHASE_2` iff `APPROVED`, else `PHASE_1`.
 *   - `effectiveLock`  — override-then-date composition (R16, R17, R18.7, R18.8).
 */
export function evaluatePhaseState(
  config: PhaseDeadlineConfig,
  status: SubmissionStatus,
  manualLockOverride: ManualLockOverride | null | undefined,
  now: Date,
): PhaseState {
  return {
    isPhase1Open: !isDeadlineReached(config.phaseOneCloseAt, now),
    isPhase2Unlocked: isPhaseTwoUnlockedWithDate(
      status,
      config.phaseTwoDueDate,
      now,
    ),
    currentPhase: currentPhaseOf(status),
    effectiveLock: effectiveApplicationLock(
      config,
      status,
      manualLockOverride,
      now,
    ),
  };
}
